import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Tag, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import api from '@/lib/api';

const PLAN_KEYS = [
  { key: 'thrive_founding', label: 'Thrive — Founding ($24.99/mo)' },
  { key: 'thrive_regular', label: 'Thrive — Regular ($49.99/mo)' },
];

const defaultForm = () => ({
  code: '',
  discount_type: 'percent_off',
  tier: 'thrive',
  duration_days: '10',
  percent_off: 10,
  applicable_plans: [],
  usage_limit: '50',
  expiry_date: '',
  label: '',
});

const formFromPromo = (pc) => ({
  code: pc.code || '',
  discount_type: pc.discount_type || 'free_trial',
  tier: pc.tier || 'thrive',
  duration_days: String(pc.duration_days ?? '10'),
  percent_off: pc.percent_off ?? 10,
  applicable_plans: pc.applicable_plans || [],
  usage_limit: pc.usage_limit == null ? '' : String(pc.usage_limit),
  // Date input expects yyyy-mm-dd
  expiry_date: pc.expiry_date ? pc.expiry_date.slice(0, 10) : '',
  label: pc.label || '',
});

/**
 * PromoCodesPanel — embedded panel for Co-Admin Dashboard with same create/list/toggle
 * capabilities as the Admin tab, scoped to codes the co-admin created.
 */
export default function PromoCodesPanel({ getAuthHeader }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingUsageCount, setEditingUsageCount] = useState(0);
  const isEditing = Boolean(editingId);

  const headers = getAuthHeader ? getAuthHeader() : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/promo/admin/codes', { headers });
      setCodes(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load promo codes');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setEditingUsageCount(0);
    setForm(defaultForm());
    setOpen(true);
  };

  const openEdit = (pc) => {
    setEditingId(pc.id);
    setEditingUsageCount(pc.usage_count || 0);
    setForm(formFromPromo(pc));
    setOpen(true);
  };

  const toggleActive = async (pc) => {
    try {
      await api.patch(`/promo/admin/codes/${pc.id}`, { is_active: !pc.is_active }, { headers });
      toast.success(pc.is_active ? 'Deactivated' : 'Activated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const submit = async () => {
    if (!isEditing && !form.code.trim()) { toast.error('Code is required'); return; }
    if (form.discount_type === 'percent_off' && form.applicable_plans.length === 0) {
      toast.error('Select at least one plan'); return;
    }
    const usageLimitNum = form.usage_limit ? parseInt(form.usage_limit, 10) : null;
    if (isEditing && usageLimitNum != null && usageLimitNum < editingUsageCount) {
      toast.error(`Usage limit can't be below current usage (${editingUsageCount})`); return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        // Edit payload — never send code/discount_type (server rejects)
        const payload = {
          usage_limit: usageLimitNum,
          expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : null,
          label: form.label.trim(),
          ...(form.discount_type === 'free_trial'
            ? { tier: form.tier, duration_days: parseInt(form.duration_days, 10) || 10 }
            : { percent_off: form.percent_off, applicable_plans: form.applicable_plans }),
        };
        await api.patch(`/promo/admin/codes/${editingId}`, payload, { headers });
        toast.success('Promo code updated');
      } else {
        const base = {
          code: form.code.trim(),
          discount_type: form.discount_type,
          usage_limit: usageLimitNum,
          expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : null,
          label: form.label.trim(),
        };
        const payload = form.discount_type === 'free_trial'
          ? { ...base, tier: form.tier, duration_days: parseInt(form.duration_days, 10) || 10 }
          : { ...base, percent_off: form.percent_off, applicable_plans: form.applicable_plans };
        await api.post('/promo/admin/codes', payload, { headers });
        toast.success(`Promo code ${payload.code} created`);
      }
      setOpen(false);
      setEditingId(null);
      setForm(defaultForm());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEditing ? 'Failed to update' : 'Failed to create'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-10" data-testid="coadmin-promo-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-navy flex items-center gap-2">
            <Tag size={20} className="text-teal" /> My Promo Codes
          </h2>
          <p className="text-sm text-text-mid">Create % off or free-trial codes. You can only manage codes you create here.</p>
        </div>
        <Button onClick={openCreate} className="bg-teal hover:bg-teal-deep rounded-full" data-testid="coadmin-create-promo-btn">
          <Plus size={16} className="mr-1" /> New Promo Code
        </Button>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-text-mid">Loading…</TableCell></TableRow>
            ) : codes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-text-mid">
                  No promo codes yet. Click <span className="font-semibold">New Promo Code</span> to create one.
                </TableCell>
              </TableRow>
            ) : codes.map(pc => {
              const isPct = pc.discount_type === 'percent_off';
              return (
                <TableRow key={pc.id} data-testid={`coadmin-promo-row-${pc.code}`}>
                  <TableCell className="font-mono font-bold text-navy">{pc.code}</TableCell>
                  <TableCell>
                    {isPct
                      ? <Badge className="bg-amber-100 text-amber-800">{pc.percent_off}% off</Badge>
                      : <Badge className="bg-purple-100 text-purple-700">Free Trial</Badge>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {isPct
                      ? (pc.applicable_plans || []).map(p => p.replace('_', ' ')).join(', ')
                      : `${pc.tier} · ${pc.duration_days}d`}
                  </TableCell>
                  <TableCell className="text-sm">{pc.usage_count} / {pc.usage_limit ?? '∞'}</TableCell>
                  <TableCell>
                    <Badge className={pc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                      {pc.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(pc)}
                        className="text-navy hover:bg-cream/50"
                        data-testid={`coadmin-edit-promo-${pc.code}`}
                      >
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleActive(pc)}
                        className={pc.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-700 hover:bg-green-50'}
                        data-testid={`coadmin-toggle-promo-${pc.code}`}
                      >
                        {pc.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="coadmin-promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">{isEditing ? 'Edit Promo Code' : 'Create Promo Code'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the usage limit, subscription tier, percentage, expiry, and label. Code name and discount type are fixed once created.'
                : 'Choose a discount type, set the rules, and we\u2019ll wire it into checkout.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. CMC-LAUNCH20"
                className="rounded-xl font-mono"
                disabled={isEditing}
                data-testid="coadmin-promo-code-input"
              />
            </div>
            <div>
              <Label>Label (internal note)</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Spring 2026 referral"
                className="rounded-xl"
              />
            </div>

            <div>
              <Label>Discount Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => !isEditing && setForm(f => ({ ...f, discount_type: 'free_trial' }))}
                  disabled={isEditing}
                  className={`rounded-xl border-2 px-3 py-2 text-sm font-medium transition-colors ${form.discount_type === 'free_trial' ? 'border-teal bg-teal/10 text-teal-deep' : 'border-border-subtle text-text-mid hover:bg-cream/30'} ${isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}
                >Free Trial</button>
                <button
                  type="button"
                  onClick={() => !isEditing && setForm(f => ({ ...f, discount_type: 'percent_off' }))}
                  disabled={isEditing}
                  className={`rounded-xl border-2 px-3 py-2 text-sm font-medium transition-colors ${form.discount_type === 'percent_off' ? 'border-teal bg-teal/10 text-teal-deep' : 'border-border-subtle text-text-mid hover:bg-cream/30'} ${isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}
                >% Off</button>
              </div>
            </div>

            {form.discount_type === 'free_trial' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tier</Label>
                  <Select value={form.tier} onValueChange={(v) => setForm(f => ({ ...f, tier: v }))}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="thrive">Thrive</SelectItem>
                      <SelectItem value="connect">Connect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Days</Label>
                  <Input
                    type="number" min="1"
                    value={form.duration_days}
                    onChange={(e) => setForm(f => ({ ...f, duration_days: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label>Discount Percentage</Label>
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {[10, 15, 20, 50].map(pct => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, percent_off: pct }))}
                        className={`rounded-xl border-2 px-3 py-2 text-sm font-bold transition-colors ${form.percent_off === pct ? 'border-teal bg-teal text-white' : 'border-border-subtle text-text-mid hover:bg-cream/30'}`}
                      >{pct}%</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Applies to which plans</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {PLAN_KEYS.map(p => {
                      const checked = form.applicable_plans.includes(p.key);
                      return (
                        <label key={p.key} data-testid={`promo-plan-${p.key}`} className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm cursor-pointer transition-colors ${checked ? 'border-teal bg-teal/10 text-teal-deep' : 'border-border-subtle text-text-mid hover:bg-cream/30'}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setForm(prev => ({
                              ...prev,
                              applicable_plans: checked
                                ? prev.applicable_plans.filter(k => k !== p.key)
                                : [...prev.applicable_plans, p.key],
                            }))}
                            className="accent-teal"
                          />
                          {p.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Usage Limit</Label>
                <Input
                  type="number" min="1"
                  value={form.usage_limit}
                  onChange={(e) => setForm(f => ({ ...f, usage_limit: e.target.value }))}
                  placeholder="Empty = unlimited"
                  className="rounded-xl"
                  data-testid="coadmin-promo-usage-input"
                />
                {isEditing && (
                  <p className="text-[11px] text-text-mid mt-1">Already used {editingUsageCount}× — cannot lower below this.</p>
                )}
              </div>
              <div>
                <Label>Expiry (optional)</Label>
                <Input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || (!isEditing && !form.code.trim()) || (form.discount_type === 'percent_off' && form.applicable_plans.length === 0)}
              className="bg-teal hover:bg-teal-deep rounded-full"
              data-testid="coadmin-promo-submit"
            >
              {saving && <Loader2 size={14} className="animate-spin mr-1" />}
              {isEditing ? 'Save Changes' : 'Create Promo Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
